import { useQuery } from "@tanstack/react-query";

import api from "@/api";

export type MergePatientFacingStatus =
  | "not_started"
  | "in_progress"
  | "merged"
  | "failed";

export type MergeStatusResponse = {
  // Lookup answer; "none" when the user has never started a merge.
  status: "none" | "in_progress" | "completed" | "failed";
  // What to show the patient; null when there is nothing to show (never linked).
  patient_facing_status: MergePatientFacingStatus | null;
  curai_patient_mrn: string | null;
};

const POLL_INTERVAL_MS = 3000;

function isTerminal(
  status: MergePatientFacingStatus | null | undefined,
): boolean {
  return status === "merged" || status === "failed";
}

/**
 * Fetches `GET /user/merge-status` for the signed-in CHAI user's account-merge
 * progress, and polls only while a run is actually active — so an idle,
 * never-merged patient (`status: "none"`) makes a single check on mount and then
 * stops, rather than hammering the gateway → clinic → Dynamo path every few
 * seconds for nothing.
 *
 * We keep polling while either the server reports `in_progress` OR the caller
 * signals `poll` (e.g. a link was just kicked off, so `none` is about to flip to
 * `in_progress`). Polling stops on a terminal state (`merged`/`failed`) and when
 * idle.
 */
export function useMergeStatus({
  enabled,
  poll = false,
}: {
  enabled: boolean;
  poll?: boolean;
}) {
  return useQuery({
    queryKey: ["user", "mergeStatus"],
    queryFn: async () => {
      const response = await api.get<MergeStatusResponse>("/user/merge-status");
      return response.data;
    },
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.patient_facing_status;
      if (isTerminal(status)) return false; // merged/failed → done
      if (status === "in_progress") return POLL_INTERVAL_MS; // active run → track it
      return poll ? POLL_INTERVAL_MS : false; // idle unless caller kicked one off
    },
  });
}
